import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

const ZoomOAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'error'>(
    'idle'
  );
  const [message, setMessage] = useState<string>('');

  const canonical = useMemo(() => `${window.location.origin}/zoom/callback`, []);

  useSEO({
    title: 'Zoom OAuth Callback - Resonow',
    description: 'Completing your Zoom connection securely with Resonow.',
    canonical,
    openGraph: {
      title: 'Zoom OAuth Callback - Resonow',
      description: 'Completing your Zoom connection securely with Resonow.',
      type: 'article',
      url: canonical,
      siteName: 'Resonow',
    },
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Zoom OAuth Callback',
        description:
          'Landing page to securely complete Zoom OAuth connection with Resonow.',
        url: canonical,
      },
    ],
  });

  useEffect(() => {
    const run = async () => {
      setStatus('processing');
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      if (!code) {
        setStatus('error');
        setMessage('Missing authorization code.');
        toast({
          variant: 'destructive',
          title: 'Zoom connection failed',
          description: 'Missing authorization code. Please try connecting again.',
        });
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('zoom-auth', {
          body: {
            action: 'handle_callback',
            code,
            state,
          },
        });

        if (error) {
          console.error('zoom-auth handle_callback error', error);
          setStatus('error');
          setMessage('Failed to complete Zoom connection.');
          toast({
            variant: 'destructive',
            title: 'Zoom connection failed',
            description: 'Please try again from your practitioner settings.',
          });
          return;
        }

        console.log('zoom-auth handle_callback success', data);
        setStatus('success');
        setMessage('Zoom connected successfully. Redirecting...');
        toast({
          title: 'Zoom connected',
          description: 'Your Zoom account has been connected successfully.',
        });
        setTimeout(() => navigate('/practitioner-dashboard'), 1000);
      } catch (e) {
        console.error('zoom-auth handle_callback exception', e);
        setStatus('error');
        setMessage('Unexpected error completing Zoom connection.');
        toast({
          variant: 'destructive',
          title: 'Unexpected error',
          description: 'Please try again from your practitioner settings.',
        });
      }
    };

    run();
  }, [navigate, toast]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-24">
        <article className="max-w-xl mx-auto text-center">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-earth-800">Zoom Connection</h1>
            <p className="text-earth-600 mt-2">
              We are securely completing your Zoom connection. This may take a moment.
            </p>
          </header>

          <section className="mt-8">
            {status === 'processing' && (
              <p className="text-earth-700">Processing authorization...</p>
            )}
            {status === 'success' && (
              <p className="text-green-700">{message}</p>
            )}
            {status === 'error' && (
              <div className="text-red-700">
                <p className="mb-4">{message}</p>
                <button
                  onClick={() => navigate('/practitioner-dashboard')}
                  className="px-4 py-2 rounded-md border"
                >
                  Go to Practitioner Dashboard
                </button>
              </div>
            )}
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default ZoomOAuthCallback;
